/* eslint-disable react/prop-types -- used across LOS + LMS layouts with optional loan props */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegCircle } from "react-icons/fa";
import { AppIcon } from "@/components/Icon";
import { ChangePasswordModal } from "@/components/Modal";
import TableCellCurrency from "@/components/Table/cell_formats/TableCellCurrency";
import { clearStorage, getStorage } from "@/utils/storage";
import { logoutRequest } from "@/services";
import { getApplicationsList } from "@/services/applications";
import {
  TEAL_AVATAR_BG,
  TEAL_AVATAR_FG,
  MENU_SEPARATOR,
  businessInitials,
  buildLoanRows,
  resolveAccountHeadlines,
} from "./headerAccountDropdownUtils";

/**
 * Account menu: loan switch (always shown), change password, logout.
 * @param {string} [loanUUID]
 * @param {object} [loanSummary]
 * @param {boolean} [showChangePassword=true]
 */
export default function HeaderAccountDropdown({ loanUUID, loanSummary, showChangePassword = true }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [isOpenChangePassword, setOpenChangePassword] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const userDetails = getStorage("user_session")?.userDetails ?? null;
  const { accountTitle, businessLine } = resolveAccountHeadlines(loanSummary, userDetails);

  const loanRows = useMemo(
    () => buildLoanRows(applications, loanUUID, loanSummary),
    [applications, loanUUID, loanSummary]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await getApplicationsList();
        if (cancelled) return;
        const list = response?.results ?? response?.data ?? [];
        setApplications(Array.isArray(list) ? list : []);
      } catch (e) {
        console.warn("HeaderAccountDropdown: failed to load applications list", e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loanUUID]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logoutRequest();
    } catch (err) {
      console.warn("Logout API failed, clearing session anyway", err);
    } finally {
      clearStorage(() => {
        navigate("/login", { replace: true });
      });
      setLoading(false);
      setOpen(false);
    }
  };

  const selectLoan = (row) => {
    if (row?.targetPath && !row.isSelected) {
      navigate(row.targetPath);
    }
    setOpen(false);
  };

  return (
    <div className="position-relative" ref={menuRef}>
      <button
        type="button"
        className="d-flex align-items-center gap-2 bg-white border-0 text-start p-0"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="fincobox-header-profile-menu"
      >
        <span
          className="rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0"
          style={{
            width: 40,
            height: 40,
            backgroundColor: TEAL_AVATAR_BG,
            color: TEAL_AVATAR_FG,
            fontSize: 13,
          }}
        >
          {businessInitials(businessLine)}
        </span>
        <div className="d-none d-sm-flex flex-column lh-sm">
          <span className="fw-bold text-dark small" data-testid="fincobox-header-profile-name">{accountTitle}</span>
          <span className="text-muted" style={{ fontSize: 12 }}>
            {businessLine}
          </span>
        </div>
        <span className="text-secondary">
          <AppIcon name={open ? "ChevronUp" : "ChevronDown"} size={14} />
        </span>
      </button>

      {open ? (
        <div
          className="position-absolute end-0 mt-2 bg-white rounded-4 overflow-hidden d-flex flex-column"
          style={{
            minWidth: 320,
            maxWidth: 420,
            maxHeight: "min(80vh, 560px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            border: `1px solid ${MENU_SEPARATOR}`,
            zIndex: 1050,
          }}
        >
          <div className="overflow-y-auto flex-grow-1">
            {loanRows.length === 0 ? (
              <div className="px-3 py-2 text-muted small">No loans to switch</div>
            ) : (
              loanRows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="w-100 d-flex align-items-start justify-content-between gap-2 px-3 py-2 bg-white border-0 text-start border-bottom"
                  style={{ borderColor: MENU_SEPARATOR }}
                  onClick={() => selectLoan(row)}
                >
                  <div className="lh-sm flex-grow-1 min-w-0">
                    <div className="fw-bold text-dark small">{row.label}</div>
                    <div className="d-flex align-items-center flex-wrap gap-1 text-muted" style={{ fontSize: 12 }}>
                      <span>Amount:</span>
                      <TableCellCurrency value={row.amount || 0} />
                    </div>
                    <div
                      className="small mb-1"
                      style={{
                        fontSize: 12,
                        color: row.isClosed
                          ? "#888"
                          : String(row.statusLabel).toLowerCase() === "active"
                            ? "#2E7D32"
                            : "#6c757d",
                      }}
                    >
                      Stage: {row.statusLabel}
                    </div>
                    {row.details?.length ? (
                      <dl className="mb-0 mt-1" style={{ fontSize: 11 }}>
                        {row.details.map((d) => (
                          <div key={d.key} className="d-flex gap-1 text-muted text-break">
                            <dt className="mb-0 fw-normal flex-shrink-0" style={{ minWidth: "7.5rem" }}>
                              {d.label}:
                            </dt>
                            <dd className="mb-0 text-dark text-opacity-75">{d.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                  <span className="flex-shrink-0 mt-1">
                    {row.isSelected ? (
                      <AppIcon name="CircleCheck" size={22} color="#2E7D32" />
                    ) : (
                      <FaRegCircle size={22} color="#BDBDBD" />
                    )}
                  </span>
                </button>
              ))
            )}
          </div>

          {showChangePassword ? (
            <button
              type="button"
              className="w-100 d-flex align-items-center gap-2 px-3 py-3 bg-white border-0 text-start text-dark border-bottom"
              style={{ borderColor: MENU_SEPARATOR }}
              onClick={() => {
                setOpenChangePassword(true);
                setOpen(false);
              }}
            >
              <span className="text-secondary">
                <AppIcon name="Lock" size={20} />
              </span>
              <span>Change password</span>
            </button>
          ) : null}

          <button
            type="button"
            className="w-100 d-flex align-items-center gap-2 px-3 py-3 bg-white border-0 text-start text-dark"
            style={{ borderTop: `1px solid ${MENU_SEPARATOR}` }}
            onClick={handleLogout}
            disabled={loading}
            data-testid="fincobox-header-logout"
          >
            <span className="text-secondary">
              <AppIcon name="Logout" size={20} />
            </span>
            <span>{loading ? "Logging out…" : "Logout"}</span>
          </button>
        </div>
      ) : null}

      {isOpenChangePassword ? (
        <ChangePasswordModal isOpen={isOpenChangePassword} toggleModal={setOpenChangePassword} />
      ) : null}
    </div>
  );
}
